import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { de, enUS as en } from 'date-fns/locale'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ActivityIndicator, Seo } from '../components'
import BarChart from '../components/react-chart/bar-chart'
import PageHeading from '../components/tailwind/PageHeading'
import {
  useLanguageContext,
  usePartnerContext,
  useUserContext
} from '../context'
import { query } from '../graphql'
import { AIVY_MEETING_URL } from '../helpers'
import { trackEvent } from '../helpers/analytics'

const TRANSLATION_LOCATION = 'pages.usage.'

// Unter diesen Schwellen bekommt die Kachel einen "Termin buchen"-CTA.
const RATING_CTA_THRESHOLD = 3.5

const locales = { de, en }

const formatDate = (iso, language) =>
  iso
    ? format(new Date(iso), 'dd.MM.yyyy', { locale: locales[language] || de })
    : '—'

const formatDateTime = (iso, language) =>
  iso
    ? format(new Date(iso), 'dd.MM.yyyy HH:mm', {
        locale: locales[language] || de
      })
    : '—'

const KpiCard = ({ value, label }) => (
  <div className='rounded-2xl border border-gray-200 bg-white p-4'>
    <div className='text-2xl font-semibold tabular-nums text-blue-600'>
      {value}
    </div>
    <div className='mt-1 text-sm text-gray-500'>{label}</div>
  </div>
)

KpiCard.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired
}

const Usage = () => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { partner } = usePartnerContext()
  const { isAdmin } = useUserContext()

  // Aktivierungs-Tracking (Rock Q3/26): Seiten-Besuch + CTA-Klicks —
  // Feature war ohne Events (Governance: kein Feature ohne _shown/_used).
  useEffect(() => {
    trackEvent('usage_page_viewed')
  }, [])

  const { status, data } = useQuery({
    queryKey: ['usage-report', partner.id],
    queryFn: () =>
      query({
        query: 'getUsageReport',
        variables: { action: 'getUsageReport', partner_id: partner.id }
      })
  })

  if (status === 'pending') {
    return (
      <div className='flex h-64 items-center justify-center'>
        <ActivityIndicator />
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className='p-6 text-sm text-gray-500'>
        {t(TRANSLATION_LOCATION + 'load_error')}
      </div>
    )
  }

  const parsed = typeof data === 'string' ? JSON.parse(data) : data
  const report = parsed?.body?.getUsageReport?.res

  if (!report) {
    return (
      <div className='p-6 text-sm text-gray-500'>
        {t(TRANSLATION_LOCATION + 'load_error')}
      </div>
    )
  }

  const { kpis, months, days, funnel } = report
  const translateEnum = (value) =>
    value
      ? t(`${TRANSLATION_LOCATION}enums.${value}`, { defaultValue: value })
      : ''

  // Bei langjährigen Partnern liefert das Backend die komplette Historie —
  // fürs Dashboard sind die letzten 24 Monate lesbar und relevant.
  const visibleMonths = months.slice(-24)
  const monthChart = {
    labels: visibleMonths.map((m) =>
      format(new Date(`${m.ym}-01`), 'MMM yy', {
        locale: locales[language] || de
      })
    ),
    datasets: [
      {
        label: t(TRANSLATION_LOCATION + 'chart_events'),
        data: visibleMonths.map((m) => m.count),
        backgroundColor: '#407BFF',
        borderRadius: 3
      }
    ]
  }

  const dayChart = {
    labels: days.map((d) => formatDate(d.date, language)),
    datasets: [
      {
        label: t(TRANSLATION_LOCATION + 'chart_team_actions'),
        data: days.map((d) => d.admin),
        backgroundColor: '#407BFF',
        borderRadius: 3
      },
      {
        label: t(TRANSLATION_LOCATION + 'chart_candidate_interactions'),
        data: days.map((d) => d.candidate),
        backgroundColor: '#B9CFFF',
        borderRadius: 3
      }
    ]
  }

  const stackedOptions = {
    responsive: true,
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } }
    },
    plugins: { legend: { position: 'bottom' } }
  }

  const { experience, speed, saved_screening, benchmark } = report
  const hiresTotal = kpis.hired + kpis.trial_survived
  const formatDuration = (hours) =>
    hours < 1
      ? t(TRANSLATION_LOCATION + 'duration_minutes', {
          count: Math.round(hours * 60)
        })
      : t(TRANSLATION_LOCATION + 'duration_hours', {
          count: Math.round(hours * 10) / 10
        })

  const highlights = [
    experience?.avg_challenge_rating != null && {
      key: 'experience',
      value: `${experience.avg_challenge_rating.toLocaleString(language)}/5 ★`,
      label: t(TRANSLATION_LOCATION + 'highlight_experience', {
        count: experience.ratings_count
      }),
      cta: experience.avg_challenge_rating < RATING_CTA_THRESHOLD
    },
    speed?.median_hours_invite_to_completed != null && {
      key: 'speed',
      value: formatDuration(speed.median_hours_invite_to_completed),
      label: t(TRANSLATION_LOCATION + 'highlight_speed', {
        count: speed.sample
      })
    },
    saved_screening?.minutes > 0 && {
      key: 'saved',
      value: `~${Math.round(saved_screening.minutes / 60)} h`,
      label: t(TRANSLATION_LOCATION + 'highlight_saved', {
        minutes: saved_screening.assumption_minutes_per_assessment
      })
    },
    hiresTotal > 0 && {
      key: 'hires',
      value: hiresTotal,
      label: t(TRANSLATION_LOCATION + 'highlight_hires', {
        trial: kpis.trial_survived
      })
    },
    benchmark?.completion_rate != null &&
      kpis.invited > 0 && {
        key: 'benchmark',
        value: `${Math.round((kpis.completed / kpis.invited) * 100)} %`,
        label: t(TRANSLATION_LOCATION + 'highlight_benchmark', {
          benchmark: Math.round(benchmark.completion_rate * 100),
          date: formatDate(benchmark.computed_at, language)
        }),
        cta: kpis.completed / kpis.invited < benchmark.completion_rate
      }
  ].filter(Boolean)

  const kpiItems = [
    { value: kpis.team, label: t(TRANSLATION_LOCATION + 'kpi_team') },
    {
      value: kpis.active_days,
      label: t(TRANSLATION_LOCATION + 'kpi_active_days')
    },
    { value: kpis.careers, label: t(TRANSLATION_LOCATION + 'kpi_careers') },
    { value: kpis.analyses, label: t(TRANSLATION_LOCATION + 'kpi_analyses') },
    { value: kpis.invited, label: t(TRANSLATION_LOCATION + 'kpi_invited') },
    { value: kpis.completed, label: t(TRANSLATION_LOCATION + 'kpi_completed') },
    { value: kpis.hired, label: t(TRANSLATION_LOCATION + 'kpi_hired') }
  ]

  return (
    <>
      <Seo title={t(TRANSLATION_LOCATION + 'seo_title')} />
      <PageHeading
        title={t(TRANSLATION_LOCATION + 'title')}
        subtitle={t(TRANSLATION_LOCATION + 'subtitle', {
          date: formatDate(report.generated_at, language)
        })}
      />

      <div className='space-y-8 px-4 pb-12 sm:px-6 lg:px-8'>
        {kpis.log_events === 0 ? (
          <div className='rounded-2xl border border-dashed border-gray-300 p-8 text-sm text-gray-500'>
            {t(TRANSLATION_LOCATION + 'empty_state')}
          </div>
        ) : (
          <>
            {highlights.length > 0 && (
              <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
                {highlights.map((item) => (
                  <div
                    key={item.key}
                    className='rounded-2xl border border-blue-100 bg-blue-50 p-5'
                  >
                    <div className='text-3xl font-semibold tabular-nums text-blue-600'>
                      {item.value}
                    </div>
                    <div className='mt-1 text-sm text-gray-600'>
                      {item.label}
                    </div>
                    {item.cta && (
                      <a
                        href={AIVY_MEETING_URL}
                        target='_blank'
                        rel='noreferrer'
                        onClick={() =>
                          trackEvent('demo_booking_link_clicked', {
                            source: 'usage_page',
                            kpi: item.label
                          })
                        }
                        className='mt-3 inline-flex items-center rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700'
                      >
                        {t(TRANSLATION_LOCATION + 'cta_innovation')}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7'>
              {kpiItems.map((item) => (
                <KpiCard
                  key={item.label}
                  value={item.value}
                  label={item.label}
                />
              ))}
            </div>

            <div className='rounded-2xl border border-gray-200 bg-white p-6'>
              <h2 className='text-md font-medium text-gray-900'>
                {t(TRANSLATION_LOCATION + 'months_title')}
              </h2>
              {months.length > visibleMonths.length && (
                <p className='mt-0.5 text-xs text-gray-500'>
                  {t(TRANSLATION_LOCATION + 'months_capped', {
                    count: visibleMonths.length
                  })}
                </p>
              )}
              <div className='mt-4 h-64'>
                <BarChart
                  data={monthChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: { grid: { display: false } },
                      y: { beginAtZero: true, ticks: { precision: 0 } }
                    },
                    plugins: { legend: { display: false } }
                  }}
                />
              </div>
            </div>

            {days.length > 0 && (
              <div className='rounded-2xl border border-gray-200 bg-white p-6'>
                <h2 className='text-md font-medium text-gray-900'>
                  {t(TRANSLATION_LOCATION + 'days_title', {
                    count: days.length
                  })}
                </h2>
                <div className='mt-4 h-64'>
                  <BarChart
                    data={dayChart}
                    options={{ ...stackedOptions, maintainAspectRatio: false }}
                  />
                </div>
              </div>
            )}

            {kpis.invited > 0 && (
              <div className='rounded-2xl border border-gray-200 bg-white p-6'>
                <h2 className='text-md font-medium text-gray-900'>
                  {t(TRANSLATION_LOCATION + 'funnel_title')}
                </h2>
                <div className='mt-4 flex flex-wrap gap-2'>
                  {Object.entries(funnel.status_counts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([statusKey, count]) => (
                      <span
                        key={statusKey}
                        className='inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700'
                      >
                        <b className='mr-1 tabular-nums'>{count}×</b>
                        {translateEnum(statusKey)}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </>
        )}

        {isAdmin && report.team && (
          <div className='rounded-2xl border border-gray-200 bg-white p-6'>
            <h2 className='text-md font-medium text-gray-900'>
              {t(TRANSLATION_LOCATION + 'team_title')}
              <span className='ml-2 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600'>
                {t(TRANSLATION_LOCATION + 'internal_badge')}
              </span>
            </h2>
            <div className='mt-4 overflow-x-auto'>
              <table className='min-w-full text-sm'>
                <thead>
                  <tr className='text-left text-xs uppercase tracking-wide text-gray-500'>
                    <th className='py-2 pr-4'>
                      {t(TRANSLATION_LOCATION + 'team_email')}
                    </th>
                    <th className='py-2 pr-4'>
                      {t(TRANSLATION_LOCATION + 'team_created')}
                    </th>
                    <th className='py-2 pr-4'>
                      {t(TRANSLATION_LOCATION + 'team_last_seen')}
                    </th>
                    <th className='py-2 pr-4'>
                      {t(TRANSLATION_LOCATION + 'team_careers')}
                    </th>
                    <th className='py-2 pr-4'>
                      {t(TRANSLATION_LOCATION + 'team_analyses')}
                    </th>
                    <th className='py-2'>
                      {t(TRANSLATION_LOCATION + 'team_applicant_updates')}
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {report.team.map((member) => (
                    <tr key={member.owner_id || member.email}>
                      <td className='py-2 pr-4'>{member.email || '—'}</td>
                      <td className='py-2 pr-4 tabular-nums'>
                        {formatDate(member.account_created, language)}
                      </td>
                      <td className='py-2 pr-4 tabular-nums'>
                        {formatDateTime(member.last_seen, language)}
                      </td>
                      <td className='py-2 pr-4 tabular-nums'>
                        {member.careers}
                      </td>
                      <td className='py-2 pr-4 tabular-nums'>
                        {member.analyses}
                      </td>
                      <td className='py-2 tabular-nums'>
                        {member.applicant_updates}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isAdmin && report.actions && (
          <div className='rounded-2xl border border-gray-200 bg-white p-6'>
            <h2 className='text-md font-medium text-gray-900'>
              {t(TRANSLATION_LOCATION + 'actions_title', {
                count: report.actions_total
              })}
              <span className='ml-2 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600'>
                {t(TRANSLATION_LOCATION + 'internal_badge')}
              </span>
            </h2>
            {report.actions_total > report.actions.length && (
              <p className='mt-1 text-xs text-gray-500'>
                {t(TRANSLATION_LOCATION + 'actions_capped', {
                  shown: report.actions.length,
                  total: report.actions_total
                })}
              </p>
            )}
            <div className='mt-4 max-h-96 overflow-x-auto overflow-y-auto'>
              <table className='min-w-full text-sm'>
                <thead>
                  <tr className='text-left text-xs uppercase tracking-wide text-gray-500'>
                    <th className='py-2 pr-4'>
                      {t(TRANSLATION_LOCATION + 'actions_at')}
                    </th>
                    <th className='py-2 pr-4'>
                      {t(TRANSLATION_LOCATION + 'actions_action')}
                    </th>
                    <th className='py-2 pr-4'>
                      {t(TRANSLATION_LOCATION + 'actions_status')}
                    </th>
                    <th className='py-2'>
                      {t(TRANSLATION_LOCATION + 'actions_actor')}
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {report.actions.map((action, index) => (
                    <tr key={`${action.at}-${index}`}>
                      <td className='whitespace-nowrap py-2 pr-4 tabular-nums'>
                        {formatDateTime(action.at, language)}
                      </td>
                      <td className='py-2 pr-4'>
                        {translateEnum(action.context)}
                      </td>
                      <td className='py-2 pr-4'>
                        {translateEnum(action.status)}
                      </td>
                      <td className='py-2'>{action.actor || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default Usage
